/**
 * AppLayout — Authenticated shell with collapsible sidebar and top header.
 *
 * Props:
 *   children  — page content
 *   title     — optional page title shown in the header
 */

import { useState, type ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  ClipboardList,
  BookOpen,
  LogOut,
  Menu,
  X,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/context/AuthContext'

// ============================================================
// Types
// ============================================================

interface AppLayoutProps {
  children: ReactNode
  title?: string
}

interface NavItem {
  label: string
  href: string
  icon: React.ElementType
}

// ============================================================
// Nav items
// ============================================================

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'New Assessment', href: '/assess', icon: ClipboardList },
  { label: 'Governance', href: '/governance', icon: BookOpen },
]

// ============================================================
// Role badge colour
// ============================================================

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-700',
  cio: 'bg-indigo-100 text-indigo-700',
  director: 'bg-blue-100 text-blue-700',
  pm: 'bg-green-100 text-green-700',
  viewer: 'bg-slate-100 text-slate-600',
  stakeholder: 'bg-amber-100 text-amber-700',
}

// ============================================================
// Sidebar
// ============================================================

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { user, role, logout } = useAuth()
  const navigate = useNavigate()

  function handleLogout() {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-slate-200 bg-white transition-all duration-200',
        collapsed ? 'w-16' : 'w-56'
      )}
    >
      {/* Logo + collapse toggle */}
      <div className="flex h-14 items-center justify-between border-b border-slate-100 px-3">
        {!collapsed && (
          <span className="truncate text-sm font-semibold text-slate-700">RCA Tool</span>
        )}
        <button
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-0.5 px-2 py-3">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon
          return (
            <NavLink
              key={item.href}
              to={item.href}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                )
              }
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          )
        })}
      </nav>

      {/* User info + logout */}
      <div className="border-t border-slate-100 px-2 py-3">
        {!collapsed && user && (
          <div className="mb-2 rounded-md bg-slate-50 px-3 py-2">
            <p className="truncate text-xs font-medium text-slate-700">{user.name}</p>
            {role && (
              <span
                className={cn(
                  'mt-0.5 inline-block rounded px-1.5 py-0.5 text-xs font-medium capitalize',
                  ROLE_COLORS[role] ?? 'bg-slate-100 text-slate-600'
                )}
              >
                {role}
              </span>
            )}
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-sm text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
          aria-label="Sign out"
        >
          <LogOut className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </aside>
  )
}

// ============================================================
// Header
// ============================================================

interface HeaderProps {
  title: string | undefined
  onMobileMenuOpen: () => void
  user: { name: string; role?: string } | null
}

function Header({ title, onMobileMenuOpen, user }: HeaderProps) {
  return (
    <header className="flex h-14 items-center border-b border-slate-200 bg-white px-4 gap-3">
      {/* Mobile hamburger */}
      <button
        onClick={onMobileMenuOpen}
        className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 md:hidden"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Page title */}
      {title && (
        <h1 className="flex-1 text-base font-semibold text-slate-800 truncate">{title}</h1>
      )}
      {!title && <div className="flex-1" />}

      {/* User chip */}
      {user && (
        <div className="hidden items-center gap-2 sm:flex">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <span className="text-sm text-slate-600 truncate max-w-[160px]">{user.name}</span>
        </div>
      )}
    </header>
  )
}

// ============================================================
// Mobile overlay sidebar
// ============================================================

interface MobileNavProps {
  open: boolean
  onClose: () => void
}

function MobileNav({ open, onClose }: MobileNavProps) {
  const { user, role, logout } = useAuth()
  const navigate = useNavigate()

  if (!open) return null

  function handleLogout() {
    logout()
    navigate('/login', { replace: true })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex md:hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Drawer */}
      <nav className="relative flex h-full w-64 flex-col bg-white shadow-xl">
        <div className="flex h-14 items-center justify-between border-b border-slate-100 px-4">
          <span className="font-semibold text-slate-700">RCA Tool</span>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-0.5 px-2 py-3">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon
            return (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium',
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-slate-600 hover:bg-slate-100'
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            )
          })}
        </div>

        <div className="border-t border-slate-100 px-2 py-3">
          {user && (
            <div className="mb-2 rounded-md bg-slate-50 px-3 py-2">
              <p className="text-xs font-medium text-slate-700">{user.name}</p>
              {role && (
                <span
                  className={cn(
                    'mt-0.5 inline-block rounded px-1.5 py-0.5 text-xs font-medium capitalize',
                    ROLE_COLORS[role] ?? 'bg-slate-100 text-slate-600'
                  )}
                >
                  {role}
                </span>
              )}
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-slate-500 hover:bg-red-50 hover:text-red-600"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </nav>
    </div>
  )
}

// ============================================================
// AppLayout
// ============================================================

export function AppLayout({ children, title }: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const { user } = useAuth()

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Desktop sidebar */}
      <div className="hidden md:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((c) => !c)}
        />
      </div>

      {/* Mobile nav overlay */}
      <MobileNav open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header
          title={title}
          onMobileMenuOpen={() => setMobileNavOpen(true)}
          user={user}
        />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  )
}
